// src/components/report_details/ReportEmployees.tsx
import React, { useState, useMemo } from 'react';
import { Eye, Search } from 'lucide-react';
import { EmployeeDetail } from '../../../types/interfaces';
import { useTranslation } from 'react-i18next';
import { POSITIONS_PRIORITY } from '../../misc/positions_employees';

interface ReportEmployeesProps {
  employees: EmployeeDetail[];
  onViewDetails?: (employee: EmployeeDetail) => void;
}

const ReportEmployees: React.FC<ReportEmployeesProps> = ({ employees, onViewDetails }) => {
  const { t } = useTranslation();

  // 1. Estado de búsqueda
  const [searchTerm, setSearchTerm] = useState('');

  // 2. Orden alfabético inicial
  const sortedEmployees = useMemo(
    () => [...employees].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [employees]
  );

  // 3. Filtrado por searchTerm en name, title o headline
  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedEmployees;
    return sortedEmployees.filter(emp =>
      [emp.name, emp.title, emp.headline]
        .filter(Boolean)
        .some(field => field!.toLowerCase().includes(term))
    );
  }, [searchTerm, sortedEmployees]);

  // 4. Priorización según lista
  const prioritizedEmployees = useMemo(() => {
    return [...filteredEmployees].sort((a, b) => {
      const getPriority = (emp: EmployeeDetail) => {
        const text = ((emp.title || '') + ' ' + (emp.headline || '')).toLowerCase();
        const idx = POSITIONS_PRIORITY.findIndex(pos =>
          text.includes(pos.toLowerCase())
        );
        return idx === -1 ? POSITIONS_PRIORITY.length : idx;
      };
      const pa = getPriority(a);
      const pb = getPriority(b);
      if (pa !== pb) return pa - pb;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [filteredEmployees]);

  // 5. Paginación
  const dataPerPage = 9;
  const totalPages  = Math.ceil(prioritizedEmployees.length / dataPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const indexLast   = currentPage * dataPerPage;
  const indexFirst  = indexLast - dataPerPage;
  const currentData = prioritizedEmployees.slice(indexFirst, indexLast);

  const goNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const goPrev = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6 flex flex-col h-full" style={{ minHeight: '650px' }}>
      {/* Título y descripción */}
      <div>
        <div className="flex items-center mb-4">
          <div className="w-3 h-10 bg-gray-800 mr-4"></div>
          <h3 className="text-3xl md:text-4xl font-bold">{t('reportEmployees.title')}</h3>
        </div>
        <p className="text-gray-700 mb-4 text-xl">{t('reportEmployees.description')}</p>

        {/* Campo de búsqueda con icono */}
        <div className="relative w-full max-w-md mb-6">
          <Search className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder={t('reportEmployees.search_placeholder')}
            className="w-full pl-10 bg-gray-100 border border-gray-300 p-3 rounded-lg shadow-sm placeholder-gray-500 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          />
        </div>
      </div>

      {/* Lista de empleados */}
      <div className="flex-grow">
        {currentData.length > 0 ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentData.map((emp, idx) => (
              <li
                key={idx}
                className="flex items-center p-4 border rounded-lg hover:shadow cursor-pointer"
                onClick={() => onViewDetails?.(emp)}
              >
                <div className="w-16 h-16">
                  <img
                    src={emp.photo_url || 'https://profile-pics.damelio.io/user-default.jpg'}
                    alt={emp.name || t('reportEmployees.employee_alt')}
                    onError={ev => { ev.currentTarget.onerror = null; ev.currentTarget.src = 'https://profile-pics.damelio.io/user-default.jpg'; }}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-bold text-lg">{emp.name}</h4>
                  <p className="text-gray-500">{emp.title || t('reportEmployees.no_title')}</p>
                  {onViewDetails && (
                    <button
                      className="text-blue-500 mt-2 inline-flex items-center"
                      onClick={() => onViewDetails(emp)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      {t('reportEmployees.view_details')}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-600 text-xl">{t('reportEmployees.no_employees')}</p>
          </div>
        )}
      </div>

      {/* Paginación */}
      {prioritizedEmployees.length > dataPerPage && (
        <div className="mt-6 flex justify-center items-center space-x-4">
          <button
            onClick={goPrev}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
          >
            {t('reportEmployees.previous')}
          </button>
          <span>{t('reportEmployees.pagination', { current: currentPage, total: totalPages })}</span>
          <button
            onClick={goNext}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
          >
            {t('reportEmployees.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportEmployees;
